import { useCallback, useRef, useState } from "react";
import { toast } from "sonner";
import { useGetAuthorizationUrl } from "@/app/servers/api/hooks";
import { useOAuthPolling } from "./use-oauth-polling";

type UseOAuthAuthorizeOptions = {
  serverId: string;
  onAuthComplete?: () => void;
};

export const useOAuthAuthorize = ({
  serverId,
  onAuthComplete,
}: UseOAuthAuthorizeOptions) => {
  const [authorizationUrl, setAuthorizationUrl] = useState<string>();
  const { mutateAsync: getAuthorizationUrl } = useGetAuthorizationUrl();

  const onAuthCompleteRef = useRef(onAuthComplete);
  onAuthCompleteRef.current = onAuthComplete;

  const handlePollingComplete = useCallback(() => {
    setAuthorizationUrl(undefined);
    onAuthCompleteRef.current?.();
  }, []);

  const { startPolling, stopPolling, isPolling } = useOAuthPolling({
    serverId,
    onAuthComplete: handlePollingComplete,
  });

  const authorize = useCallback(
    async (scopes: string[], overrideServerId?: string) => {
      const id = overrideServerId ?? serverId;
      try {
        const authResponse = await getAuthorizationUrl({
          serverId: id,
          scopes,
        });

        if (authResponse.status === "completed") {
          toast.success("Server is already authorized");
          return;
        }

        if (authResponse.url) {
          setAuthorizationUrl(authResponse.url);
          window.open(authResponse.url, "_blank", "noopener,noreferrer");
          startPolling();
        }
      } catch {
        toast.error("Failed to start authorization. Please try again.");
      }
    },
    [serverId, getAuthorizationUrl, startPolling]
  );

  const reset = useCallback(() => {
    setAuthorizationUrl(undefined);
    stopPolling();
  }, [stopPolling]);

  return { authorizationUrl, authorize, reset, isPolling };
};
